import { apiClient } from '@shared/api';
import type { ApiResponse, PagedResult } from '@shared/types';
import type { MaterialRequestDto, MaterialRequestSyncResultDto, MrsSearchResultDto, ReleasedQuantityDto } from '../types';

class MaterialRequestService {
  private readonly baseUrl = '/materialrequest';

  async getAllPaged(params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDesc?: boolean;
  }): Promise<PagedResult<MaterialRequestDto>> {
    const { data } = await apiClient.get<ApiResponse<PagedResult<MaterialRequestDto>>>(
      `${this.baseUrl}/paged`,
      { params }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch material requests');
  }

  async getAll(): Promise<MaterialRequestDto[]> {
    const { data } = await apiClient.get<ApiResponse<MaterialRequestDto[]>>(
      this.baseUrl
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch material requests');
  }

  async getPending(): Promise<MaterialRequestDto[]> {
    const { data } = await apiClient.get<ApiResponse<MaterialRequestDto[]>>(
      `${this.baseUrl}/pending`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch pending material requests');
  }

  async getById(id: number): Promise<MaterialRequestDto> {
    const { data } = await apiClient.get<ApiResponse<MaterialRequestDto>>(
      `${this.baseUrl}/${id}`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Material request not found');
  }

  async search(query: string): Promise<MrsSearchResultDto[]> {
    const { data } = await apiClient.get<ApiResponse<MrsSearchResultDto[]>>(
      `${this.baseUrl}/search`,
      { params: { query } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to search material requests');
  }

  async syncFromNetSuite(syncFromDate?: string): Promise<MaterialRequestSyncResultDto> {
    const params = syncFromDate ? { syncFromDate } : undefined;
    const { data } = await apiClient.post<ApiResponse<MaterialRequestSyncResultDto>>(
      `${this.baseUrl}/sync`,
      null,
      { params, timeout: 5 * 60 * 1000 } // 5 min — bulk sync can process thousands of records
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to sync from NetSuite');
  }

  /**
   * Gets pending material requests with items available for picklist creation.
   * Items that are fully allocated are filtered out.
   */
  async getAvailableForPicklist(): Promise<MaterialRequestDto[]> {
    const { data } = await apiClient.get<ApiResponse<MaterialRequestDto[]>>(
      `${this.baseUrl}/available-for-picklist`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch available material requests');
  }

  /**
   * Gets real-time released quantities from NetSuite for the given material request item IDs.
   * Used by the Post MIS dialog to show if items have already been issued in NetSuite.
   */
  async getReleasedQuantities(materialRequestItemIds: number[]): Promise<ReleasedQuantityDto[]> {
    const { data } = await apiClient.post<ApiResponse<ReleasedQuantityDto[]>>(
      `${this.baseUrl}/released-quantities`,
      { materialRequestItemIds }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch released quantities');
  }

  /**
   * Serves an MRS: sets DocumentStatus to Closed and calls NetSuite RESTlet.
   * Reason required when items are not fully fulfilled (partial serve).
   */
  async serveMrs(id: number, reason?: string): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(
      `${this.baseUrl}/${id}/serve`,
      { reason: reason || null }
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to serve MRS');
    }
  }

  /**
   * Cancels an MRS: sets DocumentStatus to Cancelled and calls NetSuite RESTlet.
   */
  async cancelMrs(id: number, reason?: string): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(
      `${this.baseUrl}/${id}/cancel`,
      { reason: reason || null }
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to cancel MRS');
    }
  }

  /**
   * Re-checks a single MRS against NetSuite and updates local data.
   * Flags ForAdjustment if no longer eligible, overwrites data if eligible and safe.
   */
  async resyncMrs(id: number): Promise<MaterialRequestSyncResultDto> {
    const { data } = await apiClient.post<ApiResponse<MaterialRequestSyncResultDto>>(
      `${this.baseUrl}/${id}/resync`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to resync MRS');
  }

  /**
   * Re-syncs existing material request items to update fields (Department, EndUse).
   * Development only - updates existing records without truncating database.
   */
  async resyncItems(): Promise<MaterialRequestSyncResultDto> {
    const { data } = await apiClient.post<ApiResponse<MaterialRequestSyncResultDto>>(
      `${this.baseUrl}/resync-items`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to resync items');
  }
}

export const materialRequestService = new MaterialRequestService();
